// Imports
import React, { useContext, useEffect, useState } from "react";
import { db, storage } from "../../../firebase";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { addDoc, collection, doc, Timestamp, updateDoc } from "firebase/firestore";
//Import Context
import { AppContext } from "../../../contexts/AppContext";
import { AuthContext } from "../../../contexts/AuthContext";
//Import Components
import RequestPreview from "../PurchaseForm/RequestPreview";
//Imports Helpers
import { extractNumber, formatCurrency, getSubventionNames } from "../../../shared/helpers";
import { safeFolioGeneration } from "../../../shared/folioGenerator";
import { validateForm } from "../PurchaseForm/formValidation";
// Imports Icons
import arrowSide from '../../../assets/icons/arrowSide.svg';
import purchasingReqIcon from '../../../assets/icons/purchasingReqWhite.svg';
import add from '../../../assets/icons/add.svg';
import upload from '../../../assets/icons/upload.svg';
import trash from '../../../assets/icons/delete.svg';
import warning from '../../../assets/icons/infoWhite.svg';
// Import CSS
import '../PurchaseForm/purchaseRequestForm.css';

function ServiceContractForm({ activity, establishment, pme, project, onClose, onRequestSubmitted }) {
    const { requestsList } = useContext(AppContext);
    const { userData } = useContext(AuthContext);
    const [formData, setFormData] = useState({
        folio: '',
        supereducAccount: '',
        responsibleName: userData?.permissions === 'admin' ? '' : userData?.displayName,
        responsiblePhone: userData?.phoneNumber ? userData.phoneNumber : '',
        responsiblePosition: userData?.permissions === 'admin' ? '' : userData?.department,
        observations: '',
        technicalJustification: '',
        shoppingItems: [
            { id: 1, detail: '', quantity: '', unitValue: 0, totalValue: 0 },
            { id: 2, detail: '', quantity: '', unitValue: 0, totalValue: 0 },
            { id: 3, detail: '', quantity: '', unitValue: 0, totalValue: 0 },
        ],
        attachments: {
            backups: [],
            officialDocs: []
        }
    });
    const [errors, setErrors] = useState({
        responsibleInfo: '',
        shoppingItems: '',
        officialDocs: ''
    });
    const [incompleteItems, setIncompleteItems] = useState([]);
    const [showPreview, setShowPreview] = useState(false);
    const [exceedsPmeBalance, setExceedsPmeBalance] = useState(false);
    const [requestStatus, setRequestStatus] = useState({ confirmation: false, error: false });

    const today = new Date();
    const optionsDate = {
        day: 'numeric',
        month: 'numeric',
        year: 'numeric'
    };

    useEffect(() => {
        const contenedor = document.querySelector('.purchase-request-container'); // o tu clase principal scrollable
        if (contenedor) contenedor.scrollTop = 0;
    }, []);

    const calculateTotalValue = (quantity, unitValue) => {
        const quantityNum = extractNumber(quantity)
        return quantityNum && unitValue ? quantityNum * unitValue : '';
    };

    const calculateTotalSum = () => {
        return formData.shoppingItems.reduce((sum, item) => sum + (parseFloat(item.totalValue) || 0), 0);
    };
    const pmeBalance = activity.effectiveExpense;

    const addItem = () => {
        setFormData(prevState => {
            const newId = prevState.shoppingItems.length + 1;
            return {
                ...prevState,
                shoppingItems: [...prevState.shoppingItems, { id: newId, detail: '', quantity: '', unitValue: 0, totalValue: 0 }]
            };
        });
    };

    const removeItem = (e, id) => {
        e.preventDefault();
        setFormData(prevState => {
            // Filtramos los items y reindexamos
            const updatedItems = prevState.shoppingItems
                .filter(item => item.id !== id)
                .map((item, index) => ({
                    ...item,
                    id: index + 1 // Reasignamos IDs secuencialmente
                }));

            return {
                ...prevState,
                shoppingItems: updatedItems
            };
        });
    };

    useEffect(() => {
        const generateFolio = () => {
            if (pme?.id) {
                const newFolio = safeFolioGeneration(pme.id, requestsList);
                setFormData(prevState => ({ ...prevState, folio: newFolio }));
            }
        };

        generateFolio();
    }, [pme?.id, requestsList]);

    const isFormValid = () => {
        const { isValid } = validateForm(formData, errors, incompleteItems);
        return isValid && !exceedsPmeBalance;
    };

    useEffect(() => {
        if (showPreview) return;

        const totalSum = calculateTotalSum();
        const exceeds = totalSum > pmeBalance;
        setExceedsPmeBalance(exceeds);

        if (exceeds) {
            setErrors(prev => ({
                ...prev,
                shoppingItems: `El monto total (${formatCurrency(totalSum)}) excede el saldo PME disponible (${formatCurrency(pmeBalance)})`
            }));
            alert('La suma de la solicitud excede el saldo PME disponible. Por favor, ajuste los montos o cantidades.');
        } else {
            setErrors(prev => {
                const newErrors = { ...prev };
                if (newErrors.shoppingItems?.includes('excede el saldo PME')) {
                    delete newErrors.shoppingItems;
                }
                return newErrors;
            });
        }
    }, [formData.shoppingItems, pmeBalance]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        const newFormData = {
            ...formData,
            [name]: value
        };
        setFormData(newFormData);

        const { newErrors, newIncompleteItems } = validateForm(newFormData, errors, incompleteItems);
        setErrors(newErrors);
        setIncompleteItems(newIncompleteItems);
    };

    const handleItemChange = (id, field, value) => {
        setFormData(prevState => {
            const updatedShoppingItems = prevState.shoppingItems.map(item => {
                if (item.id === id) {
                    let updatedItem = { ...item };
                    if (field === 'unitValue') {
                        // Asegurarnos de que el valor sea un número válido y no se modifique inesperadamente
                        const numericValue = parseInt(value.replace(/[^\d]/g, ''), 10) || 0;
                        updatedItem[field] = numericValue;
                    } else {
                        updatedItem[field] = value;
                    }

                    if (field === 'quantity' || field === 'unitValue') {
                        const quantity = field === 'quantity' ? value : item.quantity;
                        const unitValue = field === 'unitValue' ? updatedItem.unitValue : item.unitValue;
                        updatedItem.totalValue = calculateTotalValue(quantity, unitValue);
                    }
                    return updatedItem;
                }
                return item;
            });

            const newFormData = {
                ...prevState,
                shoppingItems: updatedShoppingItems
            };

            const { newErrors, newIncompleteItems } = validateForm(newFormData, errors, incompleteItems);
            setErrors(newErrors);
            setIncompleteItems(newIncompleteItems);

            return newFormData;
        });
    };

    const handleFileUpload = async (e, type) => {
        const files = Array.from(e.target.files);

        if (files.length === 0) return;

        const allowedTypes = [
            'application/pdf',                                                          // PDF
            'application/msword',                                                       // DOC
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document',  // DOCX
            'application/vnd.ms-excel',                                                 // XLS
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',        // XLSX
            'application/vnd.oasis.opendocument.spreadsheet',                           // ODS
            'text/csv',                                                                 // CSV
            'image/jpeg',                                                               // JPG/JPEG
            'image/jpg',                                                                // JPG alternativo
            'image/png'                                                                 // PNG
        ];

        // Filtrar archivos no válidos
        const invalidFiles = files.filter(file => !allowedTypes.includes(file.type));
        if (invalidFiles.length > 0) {
            alert(`Algunos archivos no son de un formato válido. Por favor, use PDF, DOC, DOCX, XLS, XLSX, CSV, JPG o PNG.`);
            // Continuar con los archivos válidos
        }

        const validFiles = files.filter(file => allowedTypes.includes(file.type));
        if (validFiles.length === 0) return; // No hay archivos válidos para subir

        const uploadPromises = validFiles.map(async (file) => {
            try {
                const storageRef = ref(storage, `requests/${file.name}_${Date.now()}`);
                await uploadBytes(storageRef, file);
                const downloadURL = await getDownloadURL(storageRef);
                return { fileName: file.name, fileUrl: downloadURL };
            } catch (error) {
                console.error("Error al subir el archivo: ", error);
                return null
            }
        });

        try {
            const uploadedFiles = (await Promise.all(uploadPromises)).filter(file => file !== null);
            if (uploadedFiles.length === 0) {
                alert("No se pudo subir ningún archivo. Por favor, inténtelo de nuevo.");
                return;
            }
            // Actualizar el estado con los nuevos archivos
            const newFormData = {
                ...formData,
                attachments: {
                    ...formData.attachments,
                    [type]: [...formData.attachments[type], ...uploadedFiles]
                }
            };
            setFormData(newFormData);

            // Validar el formulario actualizado
            const { newErrors, newIncompleteItems } = validateForm(newFormData, errors, incompleteItems);
            setErrors(newErrors);
            setIncompleteItems(newIncompleteItems);

            // Limpiar el input para permitir seleccionar el mismo archivo nuevamente
            e.target.value = '';
        } catch (error) {
            console.error("Error al procesar archivos:", error);
            alert("Ocurrió un error al subir los archivos. Por favor, inténtelo de nuevo.");
        }
    };

    const handleCreateRequest = (e) => {
        e.preventDefault();

        if (isFormValid() && !exceedsPmeBalance) {
            setShowPreview(true);
        }
    };

    const handleSubmit = async (selectedDepartment, folio, requestAmount) => {
        const validShoppingItems = formData.shoppingItems.filter(item =>
            item.detail.trim() !== '' &&
            item.quantity.trim() !== '' &&
            item.unitValue !== '' &&
            item.totalValue !== ''
        );

        const requestData = {
            idEstablishment: establishment.id,
            idPME: pme.id,
            idActivity: activity.id,
            idProject: project,
            typeOfRequest: 'serviceContract',
            requestDetails: {
                ...formData,
                folio: folio,
                shoppingItems: validShoppingItems,
                requestAmount: calculateTotalSum(),
                state: 'pending',
                created: Timestamp.now(),
                sentTo: selectedDepartment.value
            },
            history: [
                {
                    backups: formData.attachments,
                    timestamp: Timestamp.now(),
                    responsibleName: formData.responsibleName,
                    role: (userData.permissions && userData.permissions === 'admin') ? formData.responsiblePosition : userData.department,
                    action: 'Solicitud Contrato de Servicio creada',
                    details: 'Solicitud creada y enviada para revisión',
                    amount: calculateTotalSum(),
                    sentTo: selectedDepartment.value,

                }
            ]
        };

        try {
            // Obtiene referencia a la colección de solicitudes
            const requestsCollection = collection(db, 'requests');
            // Añade la solicitud a Firestore
            const docRef = await addDoc(requestsCollection, requestData);
            console.log("Solicitud añadida con ID: ", docRef.id);
            // Actualizar el accruedExpense de la actividad en el PME
            const pmeDocRef = doc(db, 'pme', pme.id);
            const updatedActivities = pme.activities.map(act => {
                if (act.id === activity.id) {
                    return {
                        ...act,
                        accruedExpense: act.accruedExpense + requestAmount,
                        effectiveExpense: act.totalSubsidyAmount - (act.accruedExpense + requestAmount) - act.accruedBalance
                    };
                }
                return act;
            });
            await updateDoc(pmeDocRef, { activities: updatedActivities });
            // Maneja el éxito (ej., muestra un mensaje de éxito, cierra el formulario)
            // Obtener la actividad actualizada
            const updatedActivity = updatedActivities.find(act => act.id === activity.id);
            onClose();
            setShowPreview(false);
            setRequestStatus({ confirmation: true, error: false });
            onRequestSubmitted(true, updatedActivity);
        } catch (error) {
            console.error("Error al añadir la solicitud: ", error);
            setRequestStatus({ confirmation: false, error: true });
        }
    };

    return (
        <section className={`request-form-container flex ${showPreview ? 'no-scroll' : ''}`}>
            <section className='info-establishment flex'>
                <h3>{establishment.nameEstablishment}</h3>
                <img src={arrowSide} className='icon' alt='Icono de flecha' />
                <p>{`Plan de Mejoramiento Educativo ${pme?.yearPME}`}</p>
            </section>
            <section className='form-container flex'>
                <p className='text-bold'>Cobertura Curricular</p>
                <form className='form flex' onSubmit={handleCreateRequest}>
                    <table className='data'>
                        <thead>
                            <tr>
                                <th>Establecimiento</th>
                                <th>RBD</th>
                                <th>Fecha</th>
                                <th>Folio</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td data-label='Establecimiento'>{establishment.nameEstablishment}</td>
                                <td data-label='RBD'>{establishment.rbd}</td>
                                <td data-label='Fecha'>{today.toLocaleDateString('es-Es', optionsDate)}</td>
                                <td data-label='Folio'>{formData.folio}</td>
                            </tr>
                        </tbody>
                    </table>
                    <DescriptionOfTheAction
                        activity={activity}
                        pme={pme}
                        formData={formData}
                        handleInputChange={handleInputChange}
                    />
                    <PmeAmounts
                        activity={activity}
                    />
                    <PurchaseItem
                        items={formData.shoppingItems}
                        handleInputChange={handleItemChange}
                        calculateTotalSum={calculateTotalSum}
                        addItem={addItem}
                        removeItem={removeItem}
                        incompleteItems={incompleteItems}
                    />
                    {errors.shoppingItems && <p className="error-message">{errors.shoppingItems}</p>}
                    {(!errors.shoppingItems && incompleteItems.length > 0) && <p className='error-message'>Complete o elimine los items de compra.</p>}
                    <InformationInput
                        formData={formData}
                        handleInputChange={handleInputChange}
                        isAdmin={userData?.permissions === 'admin'}
                    />
                    {errors.responsibleInfo && <p className="error-message">{errors.responsibleInfo}</p>}
                    <FileInput
                        formData={formData}
                        handleFileUpload={handleFileUpload}
                        setFormData={setFormData}
                    />
                    {errors.officialDocs && <p className="error-message">{errors.officialDocs}</p>}
                    <section className='button-box flex'>
                        <button
                            type='submit'
                            className={`button flex ${!isFormValid() ? 'disabled' : ''}`}
                            disabled={!isFormValid()}
                        >
                            <img
                                src={purchasingReqIcon}
                                className='icon'
                                alt='Icono para crear solicitud de contrato de servicios.'
                            />
                            <p>Crear Solicitud de Contrato de Servicios</p>
                        </button>
                        <p className='cancel-button' onClick={onClose}>Cancelar</p>
                        {!isFormValid() && <span className='tooltip-message flex'><img src={warning} alt='Icono de Advertencia botón deshabilitado' /><p>Faltan o hay errores en algunos campos. Revísalos.</p></span>}
                    </section>
                </form>
                {showPreview &&
                    <RequestPreview
                        formData={formData}
                        activity={activity}
                        onSubmit={handleSubmit}
                        onCancel={() => setShowPreview(false)}
                        requestAmount={calculateTotalSum()}
                        requestStatus={requestStatus}
                        establishment={establishment.id}
                        pme={pme.id}
                        typeOfRequest='serviceContract'
                    />
                }
            </section>
        </section>
    )
};

export default ServiceContractForm;

const DescriptionOfTheAction = ({ activity, pme, formData, handleInputChange }) => {
    return (
        <article className='info-box flex'>
            <span className='item flex first-item'>
                <p className='title flex'>Año de Gasto</p>
                <p className='result flex'>{pme?.yearPME}</p>
            </span>
            <span className='item flex'>
                <p className='title flex'>Tipo de Subvención</p>
                <p className='result flex'>{`${getSubventionNames(activity.subsidies)} ${pme?.yearPME}`}</p>
            </span>
            <span className='item flex'>
                <p className='title flex'>Nombre de la Acción</p>
                <p className='result flex'>{activity.nameOfAction}</p>
            </span>
            <span className='item flex'>
                <p className='title flex'>Dimensión</p>
                <p className='result flex'>{activity.dimension}</p>
            </span>
            <span className='item flex'>
                <p className='title flex'>Subdimensión</p>
                <p className='result flex'>{activity.subdimension}</p>
            </span>
            <span className='item flex last-item'>
                <p className='title flex'>Cuenta SUPEREDUC</p>
                <span className='result flex'>
                    <label className='label-hidden'>Cuenta SUPEREDUC</label>
                    <input
                        type='text'
                        name='supereducAccount'
                        className='input'
                        value={formData.supereducAccount || ''}
                        onChange={handleInputChange}
                        placeholder='Ingresa la cuenta SUPEREDUC'
                    />
                </span>
            </span>
        </article>
    );
};

const PmeAmounts = ({ activity }) => {
    return (
        <table className='pme-amounts data border'>
            <thead>
                <tr>
                    <th>Monto Acción PME</th>
                    <th>Monto PME Utilizado</th>
                    <th>Saldo PME</th>
                </tr>
            </thead>
            <tbody>
                <tr>
                    <td data-label='Monto Acción PME'>{formatCurrency(activity.totalSubsidyAmount)}</td>
                    <td data-label='Monto PME Utilizado'>{formatCurrency(activity.accruedBalance + activity.accruedExpense)}</td>
                    <td data-label='Saldo PME'>{formatCurrency(activity.effectiveExpense)}</td>
                </tr>
            </tbody>
        </table>
    );
};

const PurchaseItem = ({ items, handleInputChange, calculateTotalSum, addItem, removeItem, incompleteItems }) => {
    return (
        <>
            <table className='pme-amounts data'>
                <thead>
                    <tr>
                        <th>Item</th>
                        <th>Detalle de Compra</th>
                        <th>Cantidad</th>
                        <th>Valor Unitario</th>
                        <th>Valor Total</th>
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    {items.map(item => {
                        const incompleteItem = incompleteItems.find(incomplete => incomplete.id === item.id);
                        return (
                            <tr key={item.id}>
                                <td data-label='Item'>{item.id}</td>
                                <td data-label='Detalle de Compra'>
                                    <input
                                        type='text'
                                        className={`input ${incompleteItem && incompleteItem.missingFields.includes('detail') ? 'error' : ''}`}
                                        value={item.detail}
                                        onChange={(e) => handleInputChange(item.id, 'detail', e.target.value)}
                                        placeholder='Ingrese'
                                    />
                                </td>
                                <td data-label='Cantidad'>
                                    <input
                                        type='text'
                                        className={`input ${incompleteItem && incompleteItem.missingFields.includes('quantity') ? 'error' : ''}`}
                                        value={item.quantity}
                                        onChange={(e) => {
                                            handleInputChange(item.id, 'quantity', e.target.value);
                                            // handleInputChange(item.id, 'totalValue', calculateTotalValue(item.unitValue, e.target.value));
                                        }}
                                        placeholder='Ingrese'
                                    />
                                </td>
                                <td data-label='Valor Unitario'>
                                    <input
                                        type='text'
                                        className={`input ${incompleteItem && incompleteItem.missingFields.includes('unitValue') ? 'error' : ''}`}
                                        value={formatCurrency(item.unitValue)}
                                        onChange={(e) => {
                                            handleInputChange(item.id, 'unitValue', e.target.value);
                                            // handleInputChange(item.id, 'totalValue', calculateTotalValue(item.quantity, e.target.value));
                                        }}
                                        placeholder='Ingrese'
                                    />
                                </td>
                                <td data-label='Valor Total'>
                                    {formatCurrency(parseFloat(item.totalValue) || 0)}
                                </td>
                                <td data-label='Eliminar'>
                                    <button type='button' className='button-delete flex' onClick={(e) => removeItem(e, item.id)}>
                                        <img src={trash} className='icon' alt='Icono para eliminar un item de compra.' />
                                    </button>
                                </td>
                            </tr>
                        )

                    })}
                </tbody>
                <tfoot>
                    <tr>
                        <td colSpan='4'>SUMA SOLICITUD</td>
                        <td colSpan='2'>
                            {formatCurrency(calculateTotalSum())}
                        </td>
                    </tr>
                    <tr onClick={addItem}>
                        <td className='add-other-item flex'>
                            <img
                                src={add}
                                className='icon'
                                alt='Icono para agregar otro item a la solicitud.'
                            />
                            <p>Agregar oto item</p>
                        </td>
                    </tr>
                </tfoot>
            </table>
        </>
    );
};

const InformationInput = ({ formData, handleInputChange, isAdmin }) => {
    return (
        <article className='info-box info-box2 flex'>
            <span className='item flex first-item'>
                <p className='title flex'>Responsable de la acción</p>
                <div className='result flex'>
                    <span className='input-container flex'>
                        <label htmlFor='responsibleName' className='label-input'>Nombre (*)</label>
                        {isAdmin ? (
                            <input
                                type='text'
                                name='responsibleName'
                                className='input'
                                placeholder='Ingrese nombre del responsable'
                                value={formData.responsibleName}
                                onChange={handleInputChange}
                            />
                        ) : (
                            <p>{formData.responsibleName}</p>
                        )}

                    </span>
                    <span className='input-container flex'>
                        <label htmlFor='phone' className='label-input'>Teléfono</label>
                        <input
                            type='text'
                            name='responsiblePhone'
                            className='input'
                            placeholder='Ingrese teléfono'
                            value={formData.responsiblePhone}
                            onChange={handleInputChange}
                        />
                    </span>
                    <span className='input-container flex'>
                        <label htmlFor='responsiblePosition' className='label-input'>Cargo (*)</label>
                        {isAdmin ? (
                            <input
                                type='text'
                                name='responsiblePosition'
                                className='input'
                                placeholder='Ingrese cargo del responsable'
                                value={formData.responsiblePosition}
                                onChange={handleInputChange}
                            />
                        ) : (
                            <p>{formData.responsiblePosition}</p>
                        )}

                    </span>
                </div>
            </span>
            <span className='item middle-item flex'>
                <p className='title flex'>Observaciones</p>
                <div className='result flex'>
                    <span className='input-container flex'>
                        <label htmlFor='observations' className='label-hidden'>Observaciones</label>
                        <textarea
                            type='text'
                            name='observations'
                            className='input flex'
                            placeholder='Ingrese sus observaciones'
                            value={formData.observations}
                            onChange={handleInputChange}
                        />
                    </span>
                </div>
            </span>
            <span className='item flex last-item'>
                <p className='title flex'>Justificación técnica pedagógica</p>
                <div className='result flex'>
                    <span className='input-container flex'>
                        <label htmlFor='technicalJustification' className='label-hidden'>Justificación técnica pedagógica</label>
                        <textarea
                            type='text'
                            name='technicalJustification'
                            className='input flex'
                            placeholder='Ingrese justificación'
                            value={formData.technicalJustification}
                            onChange={handleInputChange}
                        />
                    </span>
                </div>
            </span>
        </article>
    );
};

const FileInput = ({ formData, handleFileUpload, setFormData }) => {
    const handleDeleteFile = (e, type, fileToDelete) => {
        e.preventDefault();
        e.stopPropagation();

        const updatedFiles = formData.attachments[type].filter(
            file => file.fileName !== fileToDelete.fileName ||
                file.fileUrl !== fileToDelete.fileUrl
        );

        setFormData(prevState => ({
            ...prevState,
            attachments: {
                ...prevState.attachments,
                [type]: updatedFiles
            }
        }));
    };
    return (
        <article className='info-box info-box3 flex'>
            <span className='item flex'>
                <p className='title flex'>Adjuntar Oficio (*)</p>
                <div className='result flex'>
                    <span className='input-container flex'>
                        <div className='input flex'>
                            {(formData.attachments.officialDocs.length > 0) && (
                                <ul className='file-list flex'>
                                    {formData.attachments.officialDocs.map((file, index) => (
                                        <li key={index} className='file-item' title={file.fileName}>
                                            <a href={file.fileUrl} target="_blank" rel="noopener noreferrer" className='file-name'>
                                                {file.fileName}
                                            </a>
                                            <button
                                                type='button'
                                                className='delete-file-btn'
                                                onClick={(e) => handleDeleteFile(e, 'officialDocs', file)}
                                                title='Eliminar archivo'
                                            >
                                                <img src={trash} className='icon' alt='Eliminar archivo' />
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                            <label className='button-add-backup flex' htmlFor='file-upload'>
                                <img src={upload} className='icon' alt='Icono para subir respaldo.' />
                                <p>Subir Oficio</p>
                            </label>
                            <input
                                id="file-upload"
                                type="file"
                                onChange={(e) => handleFileUpload(e, 'officialDocs')}
                                multiple
                                style={{ display: 'none' }} // Oculta el input
                            />
                        </div>
                    </span>
                </div>
            </span>
            <span className='item flex'>
                <p className='title flex'>Adjuntar Respaldos</p>
                <div className='result flex'>
                    <span className='input-container flex'>
                        <div className='input flex'>
                            {(formData.attachments.backups.length > 0) && (
                                <ul className='file-list flex'>
                                    {formData.attachments.backups.map((file, index) => (
                                        <li key={index} className='file-item'>
                                            <a href={file.fileUrl} target="_blank" rel="noopener noreferrer" className='file-name'>
                                                {file.fileName}
                                            </a>
                                            <button
                                                type='button'
                                                className='delete-file-btn'
                                                onClick={(e) => handleDeleteFile(e, 'backups', file)}
                                                title='Eliminar archivo'
                                            >
                                                <img src={trash} className='icon' alt='Eliminar archivo' />
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                            <label className='button-add-backup flex' htmlFor='backups-upload'>
                                <img src={upload} className='icon' alt='Icono para subir respaldo.' />
                                <p>Subir Respaldo</p>
                            </label>
                            <input
                                id="backups-upload"
                                type="file"
                                onChange={(e) => handleFileUpload(e, 'backups')}
                                multiple
                                style={{ display: 'none' }} // Oculta el input
                            />
                        </div>
                    </span>
                </div>
            </span>
            {/* <p className="file-formats-info">Formatos permitidos: PDF, DOC, XLS, XLSX, CSV, JPG, PNG</p> */}
        </article>
    );
};